import axios, { AxiosError, type AxiosInstance, type InternalAxiosRequestConfig } from 'axios';
import { config } from '../config/env';
import type { AuthTokens } from '../types/data-models';

interface AuthSnapshot {
  accessToken: string | null;
  refreshToken: string | null;
}

const authSnapshot: AuthSnapshot = { accessToken: null, refreshToken: null };

/** Called by AuthContext on login/logout so the interceptor stays in sync. */
export function setAuthTokens(accessToken: string | null, refreshToken: string | null) {
  authSnapshot.accessToken = accessToken;
  authSnapshot.refreshToken = refreshToken;
}

let onUnauthorized: (() => void) | null = null;

/** Hook a global handler for 401s so AuthContext can sign the user out. */
export function setUnauthorizedHandler(handler: (() => void) | null) {
  onUnauthorized = handler;
}

export const api: AxiosInstance = axios.create({ baseURL: config.apiBaseUrl });

api.interceptors.request.use((req) => {
  if (authSnapshot.accessToken) {
    req.headers.Authorization = `Bearer ${authSnapshot.accessToken}`;
  }
  return req;
});

/** Patches just the `tokens` field of the persisted auth blob, in place. */
function persistRefreshedTokens(tokens: AuthTokens) {
  try {
    const raw = localStorage.getItem(config.authStorageKey);
    if (!raw) return;
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    parsed.tokens = tokens;
    localStorage.setItem(config.authStorageKey, JSON.stringify(parsed));
  } catch {
    // Best-effort — the refreshed tokens still work for the rest of this tab's
    // session via authSnapshot even if this write fails.
  }
}

// The access token expires after ~15 minutes (JWT_ACCESS_TTL). Rather than
// treating the resulting 401 as "session over", spend the longer-lived
// refresh token to mint a new pair and replay the request — only fall back to
// logout if the refresh itself is rejected (refresh token expired/revoked).
// Concurrent 401s (e.g. several queries firing at once) share one in-flight
// refresh via `refreshPromise` so the rotating refresh token is only spent once.
let refreshPromise: Promise<string> | null = null;

function refreshAccessToken(): Promise<string> {
  if (!refreshPromise) {
    refreshPromise = (async () => {
      const refreshToken = authSnapshot.refreshToken;
      if (!refreshToken) throw new Error('No refresh token available');
      // Deliberately bypasses the `api` instance (and these interceptors) —
      // a plain axios call avoids any risk of this request recursing back
      // into the 401 handler below.
      const res = await axios.post(`${config.apiBaseUrl}/auth/refresh`, { refreshToken });
      const body = res.data as { data?: AuthTokens } & Partial<AuthTokens>;
      const tokens = body?.data ?? (body as AuthTokens);
      if (!tokens?.accessToken) throw new Error('Invalid refresh response');
      authSnapshot.accessToken = tokens.accessToken;
      authSnapshot.refreshToken = tokens.refreshToken ?? null;
      persistRefreshedTokens(tokens);
      return tokens.accessToken;
    })().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
}

api.interceptors.response.use(
  (res) => res,
  async (err: AxiosError) => {
    const original = err.config as (InternalAxiosRequestConfig & { _retried?: boolean }) | undefined;
    // A 401 from /auth/login is "bad credentials", not "session expired" —
    // don't attempt a refresh or log the user out (a form-level error is
    // about to be rendered instead).
    const isLoginAttempt = (original?.url ?? '').endsWith('/auth/login');

    if (
      err.response?.status === 401 &&
      original &&
      !isLoginAttempt &&
      !original._retried &&
      authSnapshot.refreshToken
    ) {
      original._retried = true;
      try {
        const newAccessToken = await refreshAccessToken();
        original.headers.Authorization = `Bearer ${newAccessToken}`;
        return api(original);
      } catch {
        onUnauthorized?.();
        return Promise.reject(err);
      }
    }

    if (err.response?.status === 401 && onUnauthorized && !isLoginAttempt) {
      onUnauthorized();
    }
    return Promise.reject(err);
  },
);

/**
 * Extracts the most useful error message from an axios error and ALWAYS
 * returns a string. The backend's `{ error: { code, message } }` envelope
 * would crash React if passed straight through.
 */
export function extractErrorMessage(err: unknown): string {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data as
      | {
          message?: string;
          error?: string | { code?: string; message?: string };
          errors?: Array<{ message?: string }>;
        }
      | undefined;
    if (typeof data?.message === 'string') return data.message;
    if (typeof data?.error === 'string') return data.error;
    if (data?.error && typeof data.error === 'object' && typeof data.error.message === 'string') {
      return data.error.message;
    }
    const firstFieldError = data?.errors?.[0]?.message;
    if (typeof firstFieldError === 'string') return firstFieldError;
    return err.message ?? 'Request failed';
  }
  return err instanceof Error ? err.message : 'Unknown error';
}
